import maplibregl, { type StyleSpecification } from 'maplibre-gl'
import { Protocol } from 'pmtiles'

/**
 * Basemap do mapa.
 *
 * PADRAO: estilo publico do Carto, no par claro/escuro. O basemap proprio (PMTiles no
 * R2) so entra com `VITE_BASEMAP=pmtiles`, e SO deve ser ligado depois que o arquivo
 * estiver no bucket.
 *
 * POR QUE O PADRAO MUDOU: o padrao era o PMTiles, o arquivo nunca foi enviado ao R2,
 * e `/tiles/basemap.pmtiles` responde 404. Uma fonte vetorial que falha nao derruba o
 * mapa: o MapLibre desenha as camadas que consegue, e como todas dependiam dessa fonte,
 * sobrava so a cor de fundo. O resultado e uma tela PRETA que parece um mapa escuro.
 * Apontar o padrao para algo que nao existe e um erro que se disfarca de decoracao.
 */
let registered = false

export function registerPmtilesProtocol(): void {
	if (registered) return
	const protocol = new Protocol()
	maplibregl.addProtocol('pmtiles', protocol.tile)
	registered = true
}

/**
 * O basemap PRECISA trocar junto com o tema. Ponto escuro sobre mapa claro (ou o
 * contrario) nao e uma imperfeicao de gosto: e o dado ficando ilegivel justamente
 * onde ele deveria aparecer. O Carto publica o par - `dark-matter` e `positron` -
 * com a mesma estrutura de camadas, entao a troca e de URL, nao de codigo.
 */
const CARTO = {
	escuro: 'https://basemaps.cartocdn.com/gl/dark-matter-gl-style/style.json',
	claro: 'https://basemaps.cartocdn.com/gl/positron-gl-style/style.json',
} as const

export type BasemapTheme = keyof typeof CARTO

export function basemapStyle(theme: BasemapTheme): string | StyleSpecification {
	if (import.meta.env.VITE_BASEMAP !== 'pmtiles') return CARTO[theme]
	registerPmtilesProtocol()
	return {
		version: 8,
		glyphs: 'https://protomaps.github.io/basemaps-assets/fonts/{fontstack}/{range}.pbf',
		sources: {
			protomaps: {
				type: 'vector',
				url: `pmtiles://${window.location.origin}/tiles/basemap.pmtiles`,
				attribution: '<a href="https://protomaps.com">Protomaps</a> © OpenStreetMap',
			},
		},
		layers: minimalLayers(theme),
	} satisfies StyleSpecification
}

/** Paleta do basemap proprio, um conjunto por tema. */
const SHADES = {
	escuro: {
		bg: '#12151b',
		earth: '#171b22',
		water: '#0d1117',
		road: '#262c36',
		roadMajor: '#39424f',
		label: '#7c8899',
	},
	claro: {
		bg: '#f7f8fa',
		earth: '#f2f4f7',
		water: '#dbe4ee',
		road: '#e2e6ec',
		roadMajor: '#cdd4de',
		label: '#5c6675',
	},
} as const

/**
 * Ajustes no estilo publico depois que ele carrega.
 *
 * O `positron` desenha a pegada dos PREDIOS em `#dfdfdf` sobre um fundo quase branco, e
 * a mancha resultante compete com a trajetoria justamente nos bairros densos, que e onde
 * o corredor passa. O `dark-matter` pinta a mesma camada em `transparent`, entao no tema
 * escuro o problema nunca apareceu - e por isso passou despercebido ate alguem olhar o
 * tema claro.
 *
 * Some nos DOIS temas, de proposito. O basemap deste projeto e deliberadamente pobre
 * (agua, area construida, vias, rotulo); predio nao ajuda a ler operacao de onibus, e
 * manter a mesma pobreza nos dois temas evita que um deles vire outro mapa.
 */
const CAMADAS_OCULTAS = ['building', 'building-top'] as const

export function tuneBasemap(map: maplibregl.Map): void {
	for (const id of CAMADAS_OCULTAS) {
		if (map.getLayer(id)) map.setLayoutProperty(id, 'visibility', 'none')
	}
}

/**
 * Onde inserir as camadas do deck.gl: a primeira camada de ROTULO de verdade.
 *
 * DE ONDE VEIO O DEFEITO: a regra anterior era "a primeira camada do tipo symbol". No
 * `dark-matter` isso funciona por acidente - a primeira symbol e a de indice 66, logo
 * depois da ultima camada de geometria (65), entao o dado entrava por cima de tudo. No
 * `positron` a primeira symbol e `waterway_label`, no indice 13, com CINQUENTA E TRES
 * camadas de tunel, rua, prédio e divisa DEPOIS dela. O dado ia para o indice 13 e o
 * basemap era pintado por cima: no tema claro as trajetorias sumiam sob as ruas.
 *
 * A regra certa nao e "a primeira symbol", e sim "a primeira symbol que vem depois de
 * toda a geometria". Assim o dado fica sempre acima de qualquer preenchimento ou linha
 * do mapa e sempre abaixo do bloco de rotulos - que e a ordem que se quer: nome de rua
 * por cima do rastro, rastro por cima do asfalto.
 */
export function labelAnchorId(map: maplibregl.Map): string | undefined {
	const layers = map.getStyle().layers ?? []
	let lastGeometry = -1
	for (let i = 0; i < layers.length; i++) {
		if (layers[i].type !== 'symbol') lastGeometry = i
	}
	for (let i = lastGeometry + 1; i < layers.length; i++) {
		if (layers[i].type === 'symbol') return layers[i].id
	}
	return undefined
}

/**
 * Camadas minimas do basemap. Deliberadamente pobre: agua, area construida, vias e
 * rotulo. Mapa de operacao nao compete com o dado que esta por cima dele.
 */
function minimalLayers(theme: BasemapTheme): StyleSpecification['layers'] {
	const c = SHADES[theme]
	const bg = c.bg
	return [
		{ id: 'bg', type: 'background', paint: { 'background-color': bg } },
		{
			id: 'earth',
			type: 'fill',
			source: 'protomaps',
			'source-layer': 'earth',
			paint: { 'fill-color': c.earth },
		},
		{
			id: 'water',
			type: 'fill',
			source: 'protomaps',
			'source-layer': 'water',
			paint: { 'fill-color': c.water },
		},
		{
			id: 'roads',
			type: 'line',
			source: 'protomaps',
			'source-layer': 'roads',
			paint: {
				'line-color': c.road,
				'line-width': ['interpolate', ['linear'], ['zoom'], 10, 0.4, 16, 3],
			},
		},
		{
			id: 'roads-major',
			type: 'line',
			source: 'protomaps',
			'source-layer': 'roads',
			filter: ['==', ['get', 'kind'], 'major_road'],
			paint: {
				'line-color': c.roadMajor,
				'line-width': ['interpolate', ['linear'], ['zoom'], 10, 0.8, 16, 5],
			},
		},
		// Rotulos ficam ACIMA do deck.gl (ver beforeId no overlay): texto por baixo
		// de trajetoria vira ruido.
		{
			id: 'places',
			type: 'symbol',
			source: 'protomaps',
			'source-layer': 'places',
			layout: {
				'text-field': ['get', 'name'],
				'text-font': ['Noto Sans Regular'],
				'text-size': 11,
			},
			paint: { 'text-color': c.label, 'text-halo-color': bg, 'text-halo-width': 1.2 },
		},
	]
}
